<template>
    <div class="label-content" :class="{wrap}">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <span v-html="getLabel"></span>
    </div>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {useProjectInformationStore} from '@common/stores';
    import capitalize from 'lodash-es/capitalize';
    import {storeToRefs} from 'pinia';
    import {useFormat, useTranslation} from '#imports';
    import {escapeHtml} from '@malt/sanitize';
    import type {ProjectActivityResourcePastEventsInner, ProjectActivityResourcePastEventsInnerEventTypeEnum} from '@api';

    const props = withDefaults(
        defineProps<{
            event: ProjectActivityResourcePastEventsInner;
            wrap?: boolean;
        }>(),
        {wrap: false},
    );

    const {t} = useTranslation();

    const {formatDate, formatAmount} = useFormat();

    // handle injection + formatting of pastEvent labels
    const projectInformationStore = useProjectInformationStore();
    const {projectInformation} = storeToRefs(projectInformationStore);

    function formatAmountWithDecimals(amount: string | undefined) {
        const amountNumber = Number(amount);
        return amount && !isNaN(amountNumber) ? formatAmount(projectInformation.value.contract.currency, amountNumber, {decimals: 2}) : '-';
    }

    const getTranslatedMonth = computed<string>(() => {
        if (props.event.extraData && 'yearMonth' in props.event.extraData && props.event.extraData?.yearMonth) {
            return capitalize(formatDate(new Date(props.event.extraData?.yearMonth), {month: 'long'}));
        }
        return '-';
    });

    type LabelTranslationKey<
        TEvent extends ProjectActivityResourcePastEventsInnerEventTypeEnum,
        TSuffix extends string | undefined = never,
    > = `projectclient.projectview.activity.past.events.${TEvent}${[TSuffix] extends [never] ? '' : TSuffix}`;

    function createLabelTranslation<TEvent extends ProjectActivityResourcePastEventsInnerEventTypeEnum, TSuffix extends string | undefined = never>(
        event: TEvent,
        suffix?: TSuffix,
    ): LabelTranslationKey<TEvent, TSuffix> {
        return `projectclient.projectview.activity.past.events.${event}${suffix || ''}` as LabelTranslationKey<TEvent, TSuffix>;
    }

    const getLabel = computed(() => {
        const {eventType, author, extraData: _extraData} = props.event;

        const extraData = _extraData as {[key: string]: unknown; unit: 'DAY' | 'HOUR'; paymentAmount?: string; paiementDueDate?: string};

        const escapedAuthorFirstName = escapeHtml(author.fullName.split(' ')[0]);
        switch (eventType) {
            case 'QUOTE_CREATED':
                return t(createLabelTranslation(eventType), {
                    0: escapedAuthorFirstName,
                    1: '/proposal/pdf/' + escapeHtml(extraData.quoteId) + '/1/summary',
                });
            case 'QUOTE_AMENDED':
                return t(createLabelTranslation(eventType), {
                    0: escapedAuthorFirstName,
                    1: '/proposal/pdf/' + escapeHtml(extraData.quoteId) + '/amendment/' + escapeHtml(extraData.amendmentId) + '/summary',
                });
            case 'QUOTE_ACCEPTED':
                if (author.type === 'SYSTEM') {
                    return t(createLabelTranslation(eventType, '.system'), ['/proposal/pdf/' + escapeHtml(extraData.quoteId) + '/summary']);
                }
                return t(createLabelTranslation(eventType), {
                    0: escapedAuthorFirstName,
                    1: '/proposal/pdf/' + escapeHtml(extraData.quoteId) + '/summary',
                });
            case 'CONTRACT_PROPOSED':
                return t(createLabelTranslation(eventType), {
                    0: escapedAuthorFirstName,
                    1: '/proposal/pdf/' + escapeHtml(extraData.amendmentQuoteId) + '/summary',
                });
            case 'CONTRACT_ACCEPTED':
                if (author.type === 'SYSTEM') {
                    return t(createLabelTranslation(eventType, '.system'), [
                        '/mission/pdf/' +
                            escapeHtml(projectInformation.value.projectId) +
                            '/amendment/' +
                            escapeHtml(extraData.amendmentId) +
                            '/summary',
                    ]);
                }
                return t(createLabelTranslation(eventType), {
                    0: escapedAuthorFirstName,
                    1:
                        '/mission/pdf/' +
                        escapeHtml(projectInformation.value.projectId) +
                        '/amendment/' +
                        escapeHtml(extraData.amendmentId) +
                        '/summary',
                });
            case 'CONTRACT_CANCELLED':
                return t(createLabelTranslation(eventType), {0: escapedAuthorFirstName});
            case 'MANAGER_UPDATED':
                if (author.type === 'SYSTEM') {
                    return t(createLabelTranslation(eventType, '.system'), [extraData.newManager]);
                }
                return t(createLabelTranslation(eventType), {
                    0: escapedAuthorFirstName,
                    1: escapeHtml(extraData.newManager),
                });
            case 'CLIENT_PURCHASE_ORDER_UPDATED':
                return t(createLabelTranslation(eventType), {0: escapeHtml(extraData.purchaseOrder)});
            case 'CLIENT_PURCHASE_ORDER_REMOVED':
                return t(createLabelTranslation(eventType));
            case 'PROJECT_CANCELLED':
                if (author.type === 'SYSTEM') {
                    return t(createLabelTranslation(eventType, '.system'));
                }
                return t(createLabelTranslation(eventType), {0: escapedAuthorFirstName});
            case 'PROJECT_COMPLETION_REQUESTED':
                return t(createLabelTranslation(eventType), {0: escapedAuthorFirstName});
            case 'PROJECT_TIME_AND_MATERIAL_COMPLETED':
                return t(createLabelTranslation(eventType), {0: escapedAuthorFirstName});
            case 'PROJECT_COMPLETED':
                return t(createLabelTranslation(eventType), {0: escapedAuthorFirstName});
            case 'PROJECT_COMPLETED_AUTOMATICALLY':
                return t(createLabelTranslation(eventType));
            case 'LOT_PROFORMA_EMITTED':
                return t(createLabelTranslation(eventType), {
                    0: getTranslatedMonth.value,
                    1: '/mission/pdf/' + escapeHtml(projectInformation.value.projectId) + '/lot/' + escapeHtml(extraData.lotId) + '/proforma-invoice',
                });
            case 'LOT_TO_PREPAY':
                return t(createLabelTranslation(eventType), {0: getTranslatedMonth.value, 1: extraData.workAmount});
            case 'LOT_ESTIMATED':
                return t(createLabelTranslation(eventType, `.${extraData.unit}`), {
                    0: escapedAuthorFirstName,
                    1: getTranslatedMonth.value,
                    2: extraData.workAmount,
                });
            case 'LOT_EDITED_BY_ADMIN':
                return t(createLabelTranslation(eventType, `.${extraData.unit}`), {
                    0: escapedAuthorFirstName,
                    1: getTranslatedMonth.value,
                    2: extraData.workAmount,
                });
            case 'LOT_COMPLETED':
                if (author.type === 'SYSTEM') {
                    return t(createLabelTranslation(eventType, `.${extraData.unit}.system`), [getTranslatedMonth.value, extraData.workAmount]);
                }
                return t(createLabelTranslation(eventType, `.${extraData.unit}`), {
                    0: getTranslatedMonth.value,
                    1: escapedAuthorFirstName,
                    2: extraData.workAmount,
                });
            case 'LOT_COMPLETED_AUTOMATICALLY':
                return t(createLabelTranslation(eventType, `.${extraData.unit}`), {
                    0: getTranslatedMonth.value,
                    1: extraData.workAmount,
                });
            case 'LOT_VALIDATION_DOCUMENT_EMITTED':
                return t(createLabelTranslation(eventType), {
                    0: '/invoicing/api/document/pdf/activity-report/' + escapeHtml(extraData.documentId),
                    1: getTranslatedMonth.value,
                });
            case 'INVOICE_EMITTED':
                return t(createLabelTranslation(eventType));
            case 'INVOICE_EMAIL_SENT':
                return t(createLabelTranslation(eventType), {0: getTranslatedMonth.value, 1: extraData.companyName});
            case 'INVOICE_LETTER_SENT':
                return t(createLabelTranslation(eventType), {0: getTranslatedMonth.value, 1: extraData.companyName});
            case 'INVOICE_LATE_EMAIL_SENT':
                return t(createLabelTranslation(eventType));
            case 'INVOICE_LATE_EMAIL_SENT_TO_ACCOUNTING_DEPARTMENT':
                return t(createLabelTranslation(eventType));
            case 'INVOICE_LATE_FORMAL_NOTICE_EMAIL_SENT':
                return t(createLabelTranslation(eventType));
            case 'INVOICE_LATE_FORMAL_NOTICE_REMINDER_EMAIL_SENT':
                return t(createLabelTranslation(eventType));
            case 'PREPAYMENT_LATE_EMAIL_SENT':
                return t(createLabelTranslation(eventType));
            case 'PAYMENT_BY_SEPA_DIRECT_DEBIT_INITIATED':
                return t(createLabelTranslation(eventType), {0: escapeHtml(formatAmountWithDecimals(extraData.paymentAmount))});
            case 'PAYMENT_BY_SEPA_DIRECT_DEBIT_ACTIVATED':
                return t(createLabelTranslation(eventType));
            case 'PAYMENT_BY_SEPA_DIRECT_DEBIT_DEACTIVATED':
                return t(createLabelTranslation(eventType));
            case 'PAYMENT_BY_TRANSFER_INITIATED':
                return t(createLabelTranslation(eventType));
            case 'PAYMENT_TRANSACTION_FINANCING':
                return t(createLabelTranslation(eventType), [
                    escapeHtml(formatAmountWithDecimals(extraData.paymentAmount)),
                    escapeHtml(extraData.companyName),
                    escapeHtml(formatDate(extraData.paiementDueDate)),
                ]);
            case 'PAYMENT_TRANSACTION_PAID':
                if (author.type === 'SYSTEM') {
                    return t(createLabelTranslation(eventType, '.system'), [escapeHtml(formatAmountWithDecimals(extraData.paymentAmount))]);
                }
                return t(createLabelTranslation(eventType), {
                    0: escapedAuthorFirstName,
                    1: escapeHtml(formatAmountWithDecimals(extraData.paymentAmount)),
                });
            case 'PAYMENT_LOT_TRANSACTION_PAID':
                return t(createLabelTranslation(eventType), [escapeHtml(formatAmountWithDecimals(extraData.paymentAmount))]);
            case 'PAYMENT_TRANSACTION_PROVISIONED':
                return t(createLabelTranslation(eventType), {
                    0: escapeHtml(formatAmountWithDecimals(extraData.paymentAmount)),
                });
            case 'PAYMENT_TRANSACTION_REFUNDED':
                if (author.type === 'SYSTEM') {
                    return t(createLabelTranslation(eventType, '.system'), {
                        0: escapeHtml(formatAmountWithDecimals(extraData.paymentAmount)),
                        1: getTranslatedMonth.value,
                        2: `/client-settings/${extraData.clientCompanyId}/wallet`,
                    });
                }
                return t(createLabelTranslation(eventType), {
                    0: escapeHtml(formatAmountWithDecimals(extraData.paymentAmount)),
                    1: escapedAuthorFirstName,
                });
            case 'PAYMENT_BY_CARD_REFUNDED':
                return t(createLabelTranslation(eventType), {0: escapeHtml(formatAmountWithDecimals(extraData.paymentAmount))});
            case 'CLIENT_APPRAISED_FREELANCER':
                return t(createLabelTranslation(eventType), {0: escapedAuthorFirstName});
            case 'EXPENSE_SUBMITTED':
                return t(createLabelTranslation(eventType), {0: extraData.expenseId});
            case 'EXPENSE_APPROVED':
                return t(createLabelTranslation(eventType), {0: extraData.expenseId, 1: extraData.expenseApprovalSetId});
            case 'EXPENSE_BLOCKED':
                return t(createLabelTranslation(eventType), {0: extraData.expenseId});
            case 'EXPENSE_PAID':
            case 'EXPENSE_CANCELLED':
                return t(createLabelTranslation(eventType), {0: extraData.expenseId, 1: extraData.expenseApprovalSetId});
            case 'PROJECT_CLOSED':
                return t(createLabelTranslation(eventType));
            case 'EXPENSE_BATCHED':
                return ''; // This event is not send right now
            case 'RENEWAL_APPROVED': {
                return t(createLabelTranslation(eventType, getRenewalSuffix.value), {
                    0: escapedAuthorFirstName,
                    1: getRenewalLink.value,
                });
            }
            case 'RENEWAL_ACCEPTED_BY_FREELANCER': {
                return t(createLabelTranslation(eventType, getRenewalSuffix.value), {
                    0: escapedAuthorFirstName,
                    1: getRenewalLink.value,
                });
            }
            case 'RENEWAL_INITIATED': {
                return t(createLabelTranslation(eventType), {
                    0: escapedAuthorFirstName,
                });
            }
            case 'RENEWAL_REFUSED_BY_FREELANCER': {
                return t(createLabelTranslation(eventType), {
                    0: escapedAuthorFirstName,
                    1: projectInformation.value.freelancer.firstName,
                });
            }
            case 'RENEWAL_REFUSED_BY_CLIENT': {
                return t(createLabelTranslation(eventType), {
                    0: escapedAuthorFirstName,
                    1: projectInformation.value.client.firstName,
                });
            }
            default:
                return '';
        }
    });

    const getRenewalSuffix = computed(() => ('.' + props.event.extraData.type.toLowerCase()) as '.source' | '.target');

    const getRenewalLink = computed(() => {
        return props.event.extraData.type === 'SOURCE'
            ? `/project-client/project?correlationId=${props.event.extraData.targetCorrelationId}`
            : `/project-client/project?correlationId=${props.event.extraData.sourceCorrelationId}`;
    });
</script>

<style lang="scss" scoped>
    .label-content.wrap span {
        white-space: pre-wrap;
        max-width: 100%;
    }
</style>
